import { Controller } from "@hotwired/stimulus"
import { yPositionsPatterns, type PositionPattern, IMAGE_BASE_WIDTH, CROP_PADDING, FONT_SIZE } from "../constants/menu_position_patterns"

type TemplateId = "1" | "2" | "3" | "4" | "5"
type YPositions = number[]

export default class extends Controller {
  static values = {
    templateId: String,
  }

  declare templateIdValue: TemplateId

  // line2のオフセット（line1からの相対位置）
  private readonly line2Offset = 52

  connect() {
    this.observeResize()
    this.setYPositions()
    this.setImageRange()
    this.setCssVariables()
    this.waitForImageAndUpdateScale()
  }

  private waitForImageAndUpdateScale() {
    const preview = this.element as HTMLElement
    const img = preview.querySelector('img') as HTMLImageElement | null

    if (!img) {
      // 画像が見つからない場合はフォールバック
      preview.style.setProperty('--menu-scale', '1')
      this.dispatchMenuScaleReady()
      return
    }

    // 画像が既に読み込まれているか確認
    if (img.complete && img.naturalWidth > 0) {
      // 既に読み込まれている場合は即座に計算
      this.updateScaleVariable()
      this.dispatchMenuScaleReady()
    } else {
      // 読み込まれていない場合はloadイベントを待つ
      img.addEventListener('load', () => {
        this.updateScaleVariable()
        this.dispatchMenuScaleReady()
      }, { once: true })

      // エラー時のフォールバック
      img.addEventListener('error', () => {
        preview.style.setProperty('--menu-scale', '1')
        this.dispatchMenuScaleReady()
      }, { once: true })
    }
  }

  private updateScaleVariable() {
    const preview = this.element as HTMLElement
    const img = preview.querySelector('img') as HTMLImageElement | null

    // 実表示幅
    const displayWidth = (() => {
      if (img) return img.clientWidth || img.getBoundingClientRect().width
      return 0
    })()

    // 画像の実寸幅（ベース幅）を画像から取得
    const baseWidth = (() => {
      if (img) {
        // naturalWidth を利用
        if (img.naturalWidth && img.naturalWidth > 0) return img.naturalWidth
      }
      return 0
    })()

    if (displayWidth > 0 && baseWidth > 0) {
      const scale = displayWidth / baseWidth
      preview.style.setProperty('--menu-scale', String(scale))
    } else {
      // フォールバック
      preview.style.setProperty('--menu-scale', '1')
    }
  }

  private observeResize() {
    const preview = this.element as HTMLElement

    // ResizeObserverでリサイズに追従
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => this.updateScaleVariable())
      ro.observe(preview)
      // 要素に紐づけておく
      ;(this as any)._menuScaleRO = ro
    } else {
      // フォールバック: リサイズイベント
      const handler = () => this.updateScaleVariable()
      const win = window as unknown as Window & typeof globalThis
      win.addEventListener('resize', handler)
      ;(this as any)._menuScaleResizeHandler = handler
    }
  }

  disconnect() {
    // 後始末
    const ro: ResizeObserver | undefined = (this as any)._menuScaleRO
    if (ro) ro.disconnect()
    const handler: (() => void) | undefined = (this as any)._menuScaleResizeHandler
    if (handler) {
      const win = window as unknown as Window & typeof globalThis
      win.removeEventListener('resize', handler)
    }
  }

  private dispatchMenuScaleReady() {
    const event = new CustomEvent('menu-scale:ready', {
      bubbles: true,
      cancelable: false,
    })
    this.element.dispatchEvent(event)
  }

  private setYPositions() {
    // data-input-index属性を持つ要素を取得（menu, confirm の入力群 + プレビューアイテムの双方に対応）
    const inputGroups = this.element.querySelectorAll(
      '.p-menu__preview__form__input-group[data-input-index]'
    ) as NodeListOf<HTMLElement>
    const previewItems = this.element.querySelectorAll(
      '.c-menu-preview__menu-item[data-input-index]'
    ) as NodeListOf<HTMLElement>

    // template_idに応じたy座標の配列を取得
    const templateId = (this.templateIdValue || "1") as TemplateId
    const yPositions = this.getYPositionsForTemplate(templateId)

    inputGroups.forEach(group => {
      const indexStr = group.getAttribute('data-input-index')
      if (!indexStr) return

      const index = parseInt(indexStr, 10)
      if (isNaN(index) || index < 0 || index >= yPositions.length) return

      // グループ要素のy座標を設定（line1用）
      group.style.setProperty('--y', `${yPositions[index]}px`)
    })

    // プレビューアイテムにもy座標を適用
    previewItems.forEach(item => {
      const indexStr = item.getAttribute('data-input-index')
      if (!indexStr) return

      const index = parseInt(indexStr, 10)
      if (isNaN(index) || index < 0 || index >= yPositions.length) return

      item.style.setProperty('--y', `${yPositions[index]}px`)
    })
  }

  private getYPositionsForTemplate(templateId: TemplateId): YPositions {
    // パターンマップを検索して、templateIdが含まれるパターンのy座標を返す
    for (const pattern of Object.values(yPositionsPatterns)) {
      if (pattern.ids.includes(templateId)) {
        return pattern.positions
      }
    }
    // フォールバック: デフォルトパターンの最初の値を返す
    return yPositionsPatterns["default"].positions
  }

  private getPatternForTemplate(templateId: TemplateId): PositionPattern {
    // パターンマップを検索して、templateIdが含まれるパターンを返す
    for (const pattern of Object.values(yPositionsPatterns)) {
      if (pattern.ids.includes(templateId)) {
        return pattern
      }
    }
    // フォールバック: デフォルトパターンを返す
    return yPositionsPatterns["default"]
  }

  private setImageRange() {
    const preview = this.element as HTMLElement

    // template_idに応じた画像範囲を取得
    const templateId = (this.templateIdValue || "1") as TemplateId
    const pattern = this.getPatternForTemplate(templateId)

    // positions, height から imageRange を計算
    // top: positions[0] - 100
    // bottom: positions[最後] + height + 100
    if (pattern.positions.length > 0) {
      const firstPosition = pattern.positions[0]
      const lastPosition = pattern.positions[pattern.positions.length - 1]
      const height = pattern.height

      const top = firstPosition - 100
      const bottom = lastPosition + height + 100

      preview.style.setProperty('--image-range-top', `${top}px`)
      preview.style.setProperty('--image-range-bottom', `${bottom}px`)
      // 範囲を適用
      preview.classList.add('has-image-range')
    } else {
      // 範囲が定義されていない場合はクラスを削除
      preview.classList.remove('has-image-range')
    }
  }

  private setCssVariables() {
    const preview = this.element as HTMLElement

    // template_idに応じたCSS変数を取得
    const templateId = (this.templateIdValue || "1") as TemplateId
    const pattern = this.getPatternForTemplate(templateId)

    // 数値をpx単位のCSS変数として設定
    preview.style.setProperty('--x', `${pattern.x}px`)
    preview.style.setProperty('--width', `${pattern.width}px`)
    preview.style.setProperty('--form-width', `${pattern.formWidth}px`)
    preview.style.setProperty('--font-size', `${FONT_SIZE}px`)
    preview.style.setProperty('--letter-spacing', `${pattern.letterSpacing}em`)
    preview.style.setProperty('--height', `${pattern.height}px`)
    preview.style.setProperty('--gap', `${pattern.gap}px`)
    preview.style.setProperty('--text-color', pattern.textColor)

    // クロップ値を計算（フォームモードでのみCSSで使用される）
    const x = pattern.x
    const width = pattern.width
    const cropLeft = x - CROP_PADDING
    const cropRight = IMAGE_BASE_WIDTH - (x + width + 30)
    const effectiveWidth = IMAGE_BASE_WIDTH - cropLeft - cropRight
    const imageWidth = (IMAGE_BASE_WIDTH / effectiveWidth) * 100

    // %単位に変換してCSS変数に設定
    const cropLeftPercent = (cropLeft / IMAGE_BASE_WIDTH) * 100

    preview.style.setProperty('--crop-left', `${cropLeftPercent}%`)
    preview.style.setProperty('--image-width', `${imageWidth}%`)
  }
}
